import { useEffect, useState, useCallback, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, TextNode, KEY_ENTER_COMMAND, KEY_ESCAPE_COMMAND, KEY_TAB_COMMAND } from 'lexical';
import { $createMentionNode } from './MentionNode';
import { mergeRegister } from '@lexical/utils';
import { FileText, Plus } from 'lucide-react';

interface Document {
  id: string;
  title: string;
  type: string;
}

interface MentionsPluginProps {
  documents: Document[];
  onOpenDocumentModal?: () => void;
}

export function MentionsPlugin({ documents, onOpenDocumentModal }: MentionsPluginProps): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter documents based on query
  const filteredDocuments = documents
    .filter((doc) => {
      if (!queryString) return true;
      const searchTerm = queryString.toLowerCase();
      return (
        doc.title.toLowerCase().includes(searchTerm) ||
        doc.type.toLowerCase().includes(searchTerm) ||
        doc.id.toLowerCase().includes(searchTerm)
      );
    })
    .slice(0, 10);

  // Reset selected index when filtered documents change
  useEffect(() => {
    if (selectedIndex >= filteredDocuments.length) {
      setSelectedIndex(Math.max(0, filteredDocuments.length - 1));
    }
  }, [filteredDocuments.length, selectedIndex]);

  // Insert mention
  const insertMention = useCallback(
    (doc: Document) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();

        if (!(anchorNode instanceof TextNode)) return;

        const text = anchorNode.getTextContent();
        const offset = anchor.offset;

        // Find the @ character position
        const atIndex = text.lastIndexOf('@', offset - 1);
        if (atIndex === -1) return;

        // Format the mention based on document type (matching old system)
        let mentionText = '';
        if (doc.type === 'Patent') {
          mentionText = `[PATENT_${doc.id}]`;
        } else if (doc.type === 'Publication') {
          mentionText = `[PUBLICATION_${doc.id}]`;
        } else if (doc.type === 'Application') {
          mentionText = `[APPLICATION_${doc.id}]`;
        } else if (doc.type === 'Foreign Patent') {
          mentionText = `[FOREIGN_PATENT_${doc.id}]`;
        } else if (doc.type === 'Upload') {
          mentionText = `[UPLOAD_${doc.id}]`;
        } else if (doc.type === 'MPEP') {
          mentionText = `[MPEP_${doc.id}]`;
        } else if (doc.type === 'CFR') {
          mentionText = `[CFR_${doc.id}]`;
        } else if (doc.type === 'USC') {
          mentionText = `[USC_${doc.id}]`;
        } else if (doc.type === 'Draft') {
          mentionText = `[DRAFT_${doc.title}]`;
        } else if (doc.type === 'Prosecution Document') {
          mentionText = `[PROS_DOC_${doc.id}]`;
        } else {
          mentionText = `@${doc.title}`;
        }

        // Select from @ to cursor position, then replace with mention text
        selection.setTextNodeRange(anchorNode, atIndex, anchorNode, offset);
        selection.insertText(mentionText + ' ');
      });

      setShowDropdown(false);
      setQueryString(null);
      setSelectedIndex(0);
    },
    [editor]
  );

  // Check for @ character
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          setShowDropdown(false);
          return;
        }

        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();

        if (anchorNode instanceof TextNode) {
          const text = anchorNode.getTextContent();
          const offset = anchor.offset;

          // Find the last @ before cursor
          const lastAtIndex = text.lastIndexOf('@', offset - 1);

          if (lastAtIndex !== -1 && lastAtIndex < offset) {
            // Check if there's a space between @ and cursor
            const textAfterAt = text.substring(lastAtIndex + 1, offset);
            if (!/\s/.test(textAfterAt)) {
              setQueryString(textAfterAt);
              setShowDropdown(true);
              return;
            }
          }
        }

        setShowDropdown(false);
        setQueryString(null);
      });
    });
  }, [editor]);

  // Handle keyboard navigation - use refs to avoid stale closures in the DOM listener
  const filteredDocumentsRef = useRef(filteredDocuments);
  const selectedIndexRef = useRef(selectedIndex);
  const showDropdownRef = useRef(showDropdown);
  useEffect(() => { filteredDocumentsRef.current = filteredDocuments; }, [filteredDocuments]);
  useEffect(() => { selectedIndexRef.current = selectedIndex; }, [selectedIndex]);
  useEffect(() => { showDropdownRef.current = showDropdown; }, [showDropdown]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showDropdownRef.current) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((prev) =>
          prev < filteredDocumentsRef.current.length - 1 ? prev + 1 : prev
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setShowDropdown(false);
        setQueryString(null);
      }
    };

    return editor.registerRootListener((rootElement, prevRootElement) => {
      prevRootElement?.removeEventListener('keydown', handleKeyDown, true);
      rootElement?.addEventListener('keydown', handleKeyDown, true);
    });
  }, [editor]);

  useEffect(() => {
    if (!showDropdown) return;

    const COMMAND_PRIORITY_CRITICAL = 4;

    return mergeRegister(
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (filteredDocuments.length > 0) {
            event?.preventDefault();
            insertMention(filteredDocuments[selectedIndex]);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        KEY_TAB_COMMAND,
        (event) => {
          if (filteredDocuments.length > 0) {
            event?.preventDefault();
            insertMention(filteredDocuments[selectedIndex]);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          setShowDropdown(false);
          setQueryString(null);
          return true;
        },
        COMMAND_PRIORITY_CRITICAL
      )
    );
  }, [editor, showDropdown, filteredDocuments, selectedIndex, insertMention]);

  if (!showDropdown) {
    return null;
  }

  // Show "no documents" message if there are no documents at all
  if (documents.length === 0) {
    return (
      <div className="absolute bottom-full left-0 mb-2 w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg z-50">
        <div className="p-4 text-center">
          <div className="text-sm text-gray-600 mb-3">
            No active documents to mention. Add documents to your session to reference them in chat.
          </div>
          {onOpenDocumentModal && (
            <button
              onClick={() => {
                onOpenDocumentModal();
                setShowDropdown(false);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Documents
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show "no results" message if filtered documents is empty but we have documents
  if (filteredDocuments.length === 0) {
    return (
      <div className="absolute bottom-full left-0 mb-2 w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg z-50">
        <div className="p-4 text-center">
          <div className="text-sm text-gray-600">
            No documents match "{queryString}"
          </div>
        </div>
      </div>
    );
  }

  // Determine icon color based on document type
  const getIconColor = (type: string) => {
    const normalizedType = type.toUpperCase();
    if (normalizedType === 'UPLOAD') return 'text-amber-600';
    if (normalizedType === 'MPEP') return 'text-emerald-600';
    if (normalizedType === 'CFR') return 'text-cyan-600';
    if (normalizedType === 'USC') return 'text-violet-600';
    if (normalizedType === 'DRAFT') return 'text-purple-600';
    if (normalizedType.includes('PROSECUTION')) return 'text-orange-600';
    return 'text-blue-600';
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute bottom-full left-0 mb-2 w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto z-50"
    >
      <ul className="py-1">
        {filteredDocuments.map((doc, index) => (
          <li
            key={doc.id}
            className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${
              selectedIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'
            }`}
            onClick={() => insertMention(doc)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <FileText className={`w-4 h-4 ${getIconColor(doc.type)}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{doc.title}</div>
              <div className="text-xs text-gray-500">{doc.type}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
